import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Painel IPTV Pós Pago - Melhor Servidor de revenda IPTV 2026" },
      { name: "description", content: "Busca o melhor Painel IPTV Pós Pago? Somos o servidor número 1 em estabilidade 4K. Revenda IPTV profissional com ativação imediata e suporte 24h." },
      { name: "author", content: "Lovable" },
      { property: "og:title", content: "Painel IPTV Pós Pago - Melhor Servidor de revenda IPTV 2026" },
      { property: "og:description", content: "Busca o melhor Painel IPTV Pós Pago? Somos o servidor número 1 em estabilidade 4K. Revenda IPTV profissional com ativação imediata e suporte 24h." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Lovable" },
      { name: "twitter:title", content: "Painel IPTV Pós Pago - Melhor Servidor de revenda IPTV 2026" },
      { name: "twitter:description", content: "Busca o melhor Painel IPTV Pós Pago? Somos o servidor número 1 em estabilidade 4K. Revenda IPTV profissional com ativação imediata e suporte 24h." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/ExE1QnBpEVdJLYdReaoBX2uUO1B2/social-images/social-1781179378003-LOGOS_(1)_-_Copia_(2).webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/ExE1QnBpEVdJLYdReaoBX2uUO1B2/social-images/social-1781179378003-LOGOS_(1)_-_Copia_(2).webp" },
      { name: "google-site-verification", content: "DCShWwMjvmS-C6j5atkkFd0UFLGpG0M9xcc7Bzx4oLo" },
    ],

    links: [
      {
        rel: "preconnect",
        href: "https://cdn.gpteng.co",
      },
      {
        rel: "preload",
        as: "style",
        href: appCss,
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png",
      },
      {
        rel: "shortcut icon",
        href: "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png",
      },
      {
        rel: "icon",
        sizes: "192x192",
        href: "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png",
      },
      {
        rel: "icon",
        sizes: "512x512",
        href: "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const canonicalUrl = `https://p2propaineliptv.com.br${router.state.location.pathname === '/' ? '' : router.state.location.pathname}`;

  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
      </head>
      <body className="antialiased selection:bg-electric/30 selection:text-white">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
    </QueryClientProvider>
  );
}
